/**
 * MetricSelectorView - Dropdown selector for chart metrics
 *
 * Displays a metric name with score and a shell-hosted dropdown for changing
 * the metric (SE-5568 — renders through `FixedPositionDropdownShell`, which
 * owns trigger-anchored measurement, portal + z-order, and viewport clamping;
 * replaces the gluestack Menu + RN Modal workaround).
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";

import {
  renderMetricShellDropdown,
  renderMetricShellOption,
} from "@/features/player-detail/utils/metricShellDropdown";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

/**
 * Metric option for the selector
 */
interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/**
 * Props for MetricSelectorView component
 */
interface MetricSelectorViewProps {
  /** Currently selected metric */
  readonly selectedMetric: MetricOption;
  /** Available metric options */
  readonly options: readonly MetricOption[];
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Shell open-state + positioning wiring from the Container */
  readonly dropdown: ShellDropdownWiring;
  /** Shell-hosted trigger element from the Container */
  readonly trigger: React.JSX.Element;
  /** Factory for creating stable metric select handlers */
  readonly createSelectHandler: (metric: MetricOption) => () => void;
}

/**
 * MetricSelectorView renders a metric selector with a shell-hosted dropdown
 * @param props - Component props
 * @param props.selectedMetric - Currently selected metric
 * @param props.options - Available metric options
 * @param props.isDark - Whether dark mode is active
 * @param props.dropdown - Shell open-state + positioning wiring
 * @param props.trigger - Shell-hosted trigger element
 * @param props.createSelectHandler - Factory for stable metric select handlers
 * @returns Shell dropdown containing the metric selector
 */
const MetricSelectorView = ({
  selectedMetric,
  options,
  isDark,
  dropdown,
  trigger,
  createSelectHandler,
}: MetricSelectorViewProps) =>
  renderMetricShellDropdown({
    wiring: dropdown,
    trigger,
    options: options.map(option =>
      renderMetricShellOption({
        label: option.label,
        value: option.value,
        isSelected: option.label === selectedMetric.label,
        onPress: createSelectHandler(option),
        isDark,
      })
    ),
  });

MetricSelectorView.displayName = "MetricSelectorView";

export default React.memo(MetricSelectorView);
